import { Dialog, DialogContent } from "@mui/material";
import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { HELP_TOPIC_MARKDOWN_SANITIZE_SCHEMA } from "./help-topic-markdown.constants";
import {
  HelpTopicMarkdownLightboxStyled,
  HelpTopicMarkdownStyled,
} from "./help-topic-markdown.styled";
import { resolveHelpTopicImageSrc } from "./help-topic-markdown.utils";

interface IHelpTopicMarkdownProps {
  markdown: string;
  /** Directory of the loaded `.md` file, used to resolve relative image paths. */
  markdownBasePath: string;
  dir?: "ltr" | "rtl";
}

export const HelpTopicMarkdown: FC<IHelpTopicMarkdownProps> = ({
  markdown,
  markdownBasePath,
  dir = "ltr",
}) => {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const closeLightbox = useCallback((): void => {
    setLightboxSrc(null);
  }, []);

  const components = useMemo(
    () => ({
      img: ({ src = "", alt = "" }: { src?: string; alt?: string }) => {
        const resolvedSrc = resolveHelpTopicImageSrc({
          src,
          markdownBasePath,
        });

        // Loaded eagerly: the image rides the access cookie, which only an API call refreshes, so a lazy image
        // further down a long topic could start after the token expired and stay broken until a reload.
        return (
          <button
            type="button"
            className="HelpTopicMarkdown-ImageButton"
            onClick={() => setLightboxSrc(resolvedSrc)}
            aria-label={alt || "Open image"}
          >
            <img
              src={resolvedSrc}
              alt={alt}
              className="HelpTopicMarkdown-Image"
            />
          </button>
        );
      },
      a: ({ href = "", children }: { href?: string; children?: ReactNode }) => (
        <a
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel={href.startsWith("http") ? "noreferrer noopener" : undefined}
        >
          {children}
        </a>
      ),
    }),
    [markdownBasePath],
  );

  return (
    <>
      <HelpTopicMarkdownStyled dir={dir}>
        <ReactMarkdown
          rehypePlugins={[
            [rehypeSanitize, HELP_TOPIC_MARKDOWN_SANITIZE_SCHEMA],
          ]}
          remarkPlugins={[remarkGfm, remarkBreaks]}
          components={components}
        >
          {markdown}
        </ReactMarkdown>
      </HelpTopicMarkdownStyled>

      <HelpTopicMarkdownLightboxStyled>
        <Dialog
          open={Boolean(lightboxSrc)}
          onClose={closeLightbox}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent className="HelpTopicMarkdown-LightboxContent">
            {lightboxSrc && (
              <img
                src={lightboxSrc}
                alt=""
                className="HelpTopicMarkdown-LightboxImage"
              />
            )}
          </DialogContent>
        </Dialog>
      </HelpTopicMarkdownLightboxStyled>
    </>
  );
};
